<script setup lang="ts">
import { Bell, ExternalLink, Mail, MailOpen } from 'lucide-vue-next'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import NotificationItem from '@/components/NotificationItem.vue'
import { Badge } from '@/foundation/badge'
import { Button } from '@/foundation/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/foundation/dropdown-menu'
import { useNotificationsStore } from '@/stores/notifications'
import type { Notification } from '@/types/notification'

const router = useRouter()
const notificationsStore = useNotificationsStore()
const { allNotifications, stats } = storeToRefs(notificationsStore)

const activeFilter = ref<'all' | 'unread'>('unread')
const dropdownOpen = ref(false)

onMounted(() => {
  notificationsStore.fetchNotifications()
})

const allNotificationsData = computed<Notification[]>(() => {
  return allNotifications.value
})

const unreadNotificationsData = computed<Notification[]>(() => {
  return allNotifications.value.filter((n) => !n.readAt)
})

const displayedNotifications = computed<Notification[]>(() => {
  return activeFilter.value === 'unread' ? unreadNotificationsData.value : allNotificationsData.value
})

const handleShowAll = () => {
  dropdownOpen.value = false
  setTimeout(() => {
    router.push('/notifications')
  }, 100)
}

const handleMarkAllRead = async () => {
  await notificationsStore.markAllAsRead()
}

const setFilter = (filter: 'all' | 'unread') => {
  activeFilter.value = filter
}

const handleNotificationClick = async (notification: Notification) => {
  await notificationsStore.markAsRead(notification.id)
}

const handleActionClick = (notification: Notification) => {
  if (notification.actionUrl) {
    dropdownOpen.value = false
    const actionUrl = notification.actionUrl

    setTimeout(() => {
      router.push(actionUrl)
    }, 100)
  }
}
</script>

<template>
  <DropdownMenu v-model:open="dropdownOpen">
    <DropdownMenuTrigger as-child>
      <slot name="trigger" :unread-count="stats.unread">
        <Button variant="ghost" size="icon" class="relative">
          <Bell class="h-5 w-5" />
          <span
            v-if="stats.unread > 0"
            class="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white"
          >
            {{ stats.unread }}
          </span>
        </Button>
      </slot>
    </DropdownMenuTrigger>
    <DropdownMenuContent
      align="end"
      class="w-[380px] max-w-[calc(100vw-2rem)] p-0 bg-background dark:bg-background"
      :side-offset="8"
      :align-offset="10"
    >
      <div class="border-b border-border dark:border-border px-3 py-2.5">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-1.5">
            <h3 class="text-sm font-medium">Notifications</h3>
            <Badge v-if="stats.unread > 0" variant="destructive" class="h-4 px-1 text-[10px] font-medium">
              {{ stats.unread }}
            </Badge>
          </div>
          <Button
            variant="ghost"
            size="sm"
            class="h-7 px-2 text-xs pt-0.5 hover:bg-accent dark:hover:bg-accent"
            @click="handleShowAll"
          >
            Show All
            <ExternalLink class="ml-1 mb-1 !h-4 !w-4" />
          </Button>
        </div>
      </div>

      <div class="border-b border-border dark:border-border px-3 py-2.5 bg-muted/30 dark:bg-muted/30">
        <div class="flex items-center justify-between gap-3">
          <div class="flex gap-1 bg-background-light rounded-full p-1">
            <Button
              variant="ghost"
              size="sm"
              class="h-5 px-4 text-xs font-medium rounded-full transition-all"
              :class="
                activeFilter === 'all'
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'bg-transparent text-gray-500 dark:text-gray-400 hover:bg-transparent'
              "
              @click="setFilter('all')"
            >
              All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              class="h-5 px-4 text-xs font-medium rounded-full transition-all"
              :class="
                activeFilter === 'unread'
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'bg-transparent text-gray-500 dark:text-gray-400 hover:bg-transparent'
              "
              @click="setFilter('unread')"
            >
              Unread<span v-if="stats.unread > 0" class="text-[10px] -ml-0.5">({{ stats.unread }})</span>
            </Button>
          </div>
          <Button
            v-if="stats.unread > 0"
            variant="default"
            size="sm"
            class="h-7 px-3 text-[11px] font-medium gap-1"
            @click="handleMarkAllRead"
          >
            <MailOpen class="h-3 w-3" />
            Read All
          </Button>
        </div>
      </div>

      <div
        v-if="displayedNotifications.length > 0"
        class="max-h-[280px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent"
      >
        <div class="divide-y divide-border dark:divide-border py-2.5 px-1 space-y-2">
          <NotificationItem
            v-for="notification in displayedNotifications"
            :key="notification.id"
            :notification="notification"
            compact
            @click="handleNotificationClick"
            @action-click="handleActionClick"
          />
        </div>
      </div>

      <div v-else class="px-4 py-12 text-center">
        <Mail class="mx-auto h-12 w-12 text-muted-foreground/50 dark:text-muted-foreground/50" />
        <p class="mt-2 text-sm text-muted-foreground dark:text-muted-foreground">
          {{ activeFilter === 'unread' ? 'No unread notifications' : 'No notifications' }}
        </p>
      </div>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
